import { http, HttpResponse } from 'msw';
import { findUserById } from '../data/users';
// import { ApiResponse } from '../../types/api';
import { User } from '../../types';

// Helper function to create error responses
const createErrorResponse = (message: string, code: number, status: number) => {
  return HttpResponse.json({
    success: false,
    data: null,
    message,
    code
  }, { status });
};

// Helper function to extract user ID from token
const getUserIdFromToken = (authHeader: string | null): string | null => {
  if (!authHeader || !authHeader.startsWith('Bearer ')) {
    return null;
  }
  
  const token = authHeader.substring(7);
  const tokenMatch = token.match(/mock-jwt-token-(\d+)-/);
  return tokenMatch ? tokenMatch[1] : null;
};

export const userHandlers = [
  // Get current user profile
  http.get('/api/v1/user/me', async ({ request }) => {
    try {
      const authHeader = request.headers.get('Authorization');
      const userId = getUserIdFromToken(authHeader);
      
      if (!userId) {
        return createErrorResponse('Unauthorized', 401, 401);
      }
      
      const user = findUserById(userId);
      
      if (!user) {
        return createErrorResponse('User not found', 404, 404);
      }
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 300));
      
      return HttpResponse.json({
        success: true,
        data: user,
        message: 'User profile retrieved successfully'
      });
      
    } catch (error) {
      return createErrorResponse('Failed to retrieve user profile', 500, 500);
    }
  }),

  // Get user by ID
  http.get('/api/v1/user/:userId', async ({ request, params }) => {
    try {
      const authHeader = request.headers.get('Authorization');
      const currentUserId = getUserIdFromToken(authHeader);
      
      if (!currentUserId) {
        return createErrorResponse('Unauthorized', 401, 401);
      }
      
      const { userId } = params;
      const user = findUserById(userId as string);
      
      if (!user) {
        return createErrorResponse('User not found', 404, 404);
      }
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 400));
      
      return HttpResponse.json({
        success: true,
        data: user,
        message: 'User details retrieved successfully'
      });
      
    } catch (error) {
      return createErrorResponse('Failed to retrieve user details', 500, 500);
    }
  }),

  // Update user profile
  http.put('/api/v1/user/profile', async ({ request }) => {
    try {
      const authHeader = request.headers.get('Authorization');
      const userId = getUserIdFromToken(authHeader);
      
      if (!userId) {
        return createErrorResponse('Unauthorized', 401, 401);
      }
      
      const user = findUserById(userId);
      
      if (!user) {
        return createErrorResponse('User not found', 404, 404);
      }
      
      const updateData = await request.json() as Partial<User>;
      
      // Simulate profile update (in real app, this would update the database)
      const updatedUser: User = {
        ...user,
        ...updateData,
        id: user.id, // Prevent ID from being changed
        updatedAt: new Date().toISOString()
      };
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 600));
      
      return HttpResponse.json({
        success: true,
        data: updatedUser,
        message: 'Profile updated successfully'
      });
      
    } catch (error) {
      return createErrorResponse('Failed to update profile', 500, 500);
    }
  })
];